import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { SearchPicker } from '@/components/SearchPicker';
import { toast } from 'sonner';
import {
  Plus, Copy, ExternalLink, Layers, ChevronRight, ChevronLeft,
  Building2, MapPin, Send, Users, Info, Check, Phone,
  FileText, Calendar, X, Loader2, MessageSquare, Mail, RefreshCw, Ban, RotateCcw, Trash2,
} from 'lucide-react';

// ---------- Types ----------
interface Instruction {
  id: string;
  template_id: string;
  template_name: string;
  template_version: number;
  client_id?: string | null;
  client_name: string;
  client_address?: string;
  site_id?: string | null;
  site_name?: string | null;
  site_reference: string;
  method: 'on_site' | 'remote_self';
  status: string;
  public_link: string;
  issued_at: string;
  due_date?: string | null;
  surveyor_email?: string | null;
  recipient_name?: string | null;
  recipient_phone?: string | null;
  recipient_email?: string | null;
  last_sms_sent_at?: string | null;
}
interface Template { id: string; name: string; current_published_version?: number }
interface ClientRow {
  id: string;
  name: string;
  trading_name?: string;
  broker_name?: string | null;
  physical_address?: string;
  site_count?: number;
}
interface SiteRow {
  id: string;
  name: string;
  site_reference?: string | null;
  client_id?: string | null;
  physical_address?: string | null;
  city?: string | null;
  province?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  site_contact_name?: string | null;
  site_contact_phone?: string | null;
}
interface ClientUserRow {
  id: string;
  full_name: string;
  phone: string;
  email?: string | null;
  role?: string;
  status?: string;
}

const methodBadge = (m: string): React.ReactNode => m === 'remote_self'
  ? <Badge variant="blue" className="text-[10px]">Remote</Badge>
  : <Badge variant="muted" className="text-[10px]">On-site</Badge>;

const statusBadge = (s: string): React.ReactNode => {
  if (s === 'submitted' || s === 'approved') return <Badge variant="success" className="text-[10px]">{s}</Badge>;
  if (s === 'in_progress') return <Badge variant="warning" className="text-[10px]">In progress</Badge>;
  if (s === 'rejected') return <Badge variant="warning" className="text-[10px]">Rejected</Badge>;
  if (s === 'cancelled') return <Badge variant="destructive" className="text-[10px]">Cancelled</Badge>;
  return <Badge variant="muted" className="text-[10px]">{s}</Badge>;
};

// ---------- Wizard state ----------
interface WizardForm {
  template_id: string;
  client_id: string;
  site_id: string;
  method: 'on_site' | 'remote_self';
  surveyor_email: string;
  recipient_user_id: string;   // '' if none
  recipient_name: string;
  recipient_phone: string;
  recipient_email: string;
  due_date: string;
  notes: string;
}
const BLANK: WizardForm = {
  template_id: '', client_id: '', site_id: '',
  method: 'on_site', surveyor_email: '',
  recipient_user_id: '', recipient_name: '', recipient_phone: '', recipient_email: '',
  due_date: '', notes: '',
};

export const InstructionsPage: React.FC = () => {
  const nav = useNavigate();

  const [items, setItems] = useState<Instruction[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [surveyorHistory, setSurveyorHistory] = useState<string[]>([]);

  const [creating, setCreating] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState<WizardForm>(BLANK);
  const [issuing, setIssuing] = useState(false);
  const [sendingSmsFor, setSendingSmsFor] = useState<string | null>(null);
  const [resendingFor, setResendingFor] = useState<string | null>(null);
  const [clientUsers, setClientUsers] = useState<ClientUserRow[]>([]);

  // ---------- Data loading ----------
  const loadList = async (): Promise<void> => {
    const r = await api.get<Instruction[]>('/instructions');
    setItems(r.data);
  };
  // ---- Master-only hard delete ----
  const { user, role, impersonating } = useAuth();
  const isMasterActor = Boolean(impersonating || user?.context === 'master' || role?.key === 'master_admin');
  const [selIds, setSelIds] = useState<Set<string>>(new Set());
  const [delOpen, setDelOpen] = useState(false);
  const [delText, setDelText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const toggleSel = (id: string): void => setSelIds((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const doHardDelete = async (): Promise<void> => {
    setDeleting(true);
    try {
      const r = await api.post<{ count: number; deleted: Record<string, number> }>(
        '/master/hard-delete/instructions', { ids: [...selIds], confirm: delText });
      toast.success(`Permanently deleted ${r.data.count} instruction(s)`);
      setSelIds(new Set()); setDelOpen(false); setDelText('');
      await loadList();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Delete failed');
    } finally { setDeleting(false); }
  };
  const loadStatic = async (): Promise<void> => {
    try {
      const [t, c, s, hist] = await Promise.all([
        api.get<Template[]>('/templates'),
        api.get<ClientRow[]>('/clients'),
        api.get<SiteRow[]>('/sites?limit=10000&fields=picker'),
        api.get<string[]>('/instructions/surveyor-emails'),
      ]);
      setTemplates(t.data);
      setClients(c.data);
      setSites(s.data);
      setSurveyorHistory(hist.data || []);
      if (!form.template_id && t.data.length) {
        const published = t.data.find((x) => x.current_published_version) || t.data[0];
        setForm((f) => ({ ...f, template_id: published.id }));
      }
    } catch {
      toast.error('Could not load pickers');
    }
  };
  useEffect(() => { void loadList(); void loadStatic(); }, []);

  // Whenever the client changes, refresh the client-user whitelist so
  // the Remote-self recipient picker is populated.
  useEffect(() => {
    if (!form.client_id) { setClientUsers([]); return; }
    void (async () => {
      try {
        const r = await api.get<ClientUserRow[]>(`/clients/${form.client_id}/users`);
        setClientUsers((r.data || []).filter((u) => u.status !== 'revoked'));
      } catch { setClientUsers([]); }
    })();
  }, [form.client_id]);

  // ---------- Derived data ----------
  const selectedClient = useMemo(() => clients.find((c) => c.id === form.client_id) || null, [clients, form.client_id]);
  const selectedSite = useMemo(() => sites.find((s) => s.id === form.site_id) || null, [sites, form.site_id]);
  const sitesOfClient = useMemo(() =>
    sites.filter((s) => s.client_id === form.client_id), [sites, form.client_id]);
  const selectedTemplate = useMemo(() => templates.find((t) => t.id === form.template_id) || null, [templates, form.template_id]);

  // ---------- Actions ----------
  const startIssue = (): void => {
    setForm({ ...BLANK, template_id: form.template_id || (templates[0]?.id ?? '') });
    setStep(1);
    setCreating(true);
  };
  const cancelIssue = (): void => { setCreating(false); setStep(1); };

  const pickRecipientUser = (u: ClientUserRow | null): void => {
    if (!u) {
      setForm((f) => ({ ...f, recipient_user_id: '', recipient_name: '', recipient_phone: '', recipient_email: '' }));
      return;
    }
    setForm((f) => ({
      ...f,
      recipient_user_id: u.id,
      recipient_name: u.full_name,
      recipient_phone: u.phone,
      recipient_email: u.email || '',
    }));
  };

  const issue = async (): Promise<void> => {
    if (!form.template_id || !form.site_id) { toast.error('Pick a template and a site'); return; }
    setIssuing(true);
    try {
      const payload: Record<string, unknown> = {
        template_id: form.template_id,
        client_id: form.client_id || undefined,
        site_id: form.site_id,
        method: form.method,
        surveyor_email: form.method === 'on_site' ? (form.surveyor_email || undefined) : undefined,
        recipient_user_id: form.method === 'remote_self' ? (form.recipient_user_id || undefined) : undefined,
        recipient_name: form.method === 'remote_self' ? (form.recipient_name || undefined) : undefined,
        recipient_phone: form.method === 'remote_self' ? (form.recipient_phone || undefined) : undefined,
        recipient_email: form.method === 'remote_self' ? (form.recipient_email || undefined) : undefined,
        due_date: form.due_date || undefined,
        notes: form.notes || undefined,
      };
      const r = await api.post<Instruction>('/instructions', payload);
      toast.success(`Instruction ${r.data.id} issued`);
      setCreating(false);
      setStep(1);
      setForm(BLANK);
      await loadList();
      // Refresh surveyor autocomplete history if a new email was used.
      if (form.surveyor_email && !surveyorHistory.includes(form.surveyor_email)) {
        setSurveyorHistory((h) => [form.surveyor_email, ...h]);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to issue instruction';
      toast.error(String(msg));
    } finally { setIssuing(false); }
  };

  const copyLink = async (l: string): Promise<void> => {
    await navigator.clipboard.writeText(l);
    toast.success('Link copied to clipboard');
  };

  const sendSms = async (iid: string): Promise<void> => {
    setSendingSmsFor(iid);
    try {
      await api.post(`/instructions/${iid}/send-sms`);
      toast.success('SMS dispatched');
      await loadList();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'SMS failed';
      toast.error(String(msg));
    } finally { setSendingSmsFor(null); }
  };

  const resend = async (iid: string): Promise<void> => {
    setResendingFor(iid);
    try {
      const r = await api.post<{ outcomes?: Record<string, { ok?: boolean }> }>(`/instructions/${iid}/resend`);
      const o = r.data?.outcomes || {};
      const sent = Object.entries(o).filter(([, v]) => v?.ok).map(([c]) => c.toUpperCase());
      toast.success(sent.length ? `Survey link resent via ${sent.join(' + ')}` : 'Resend attempted (check delivery log)');
      await loadList();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Resend failed';
      toast.error(String(msg));
    } finally { setResendingFor(null); }
  };

  const cancelInstruction = async (iid: string): Promise<void> => {
    if (!confirm('Cancel this survey? The public link will stop working. You can re-activate it later.')) return;
    const reason = window.prompt('Reason for cancelling (optional):') || undefined;
    try {
      await api.post(`/instructions/${iid}/cancel`, { reason });
      toast.success('Survey cancelled — link disabled');
      await loadList();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Cancel failed';
      toast.error(String(msg));
    }
  };

  const reactivateInstruction = async (iid: string): Promise<void> => {
    try {
      await api.post(`/instructions/${iid}/reactivate`);
      toast.success('Survey re-activated — link live again');
      await loadList();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Re-activate failed';
      toast.error(String(msg));
    }
  };

  // ---------- Render helpers ----------
  const StepPill: React.FC<{ n: 1 | 2 | 3; label: string; icon: React.ReactNode }> = ({ n, label, icon }) => (
    <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border text-xs font-body ${step === n
      ? 'bg-raptor-blue text-white border-raptor-blue'
      : step > n
        ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
        : 'bg-white text-raptor-navy/70 border-border/60'}`}>
      {step > n ? <Check size={12} /> : icon}
      <span className="hidden sm:inline">{label}</span>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="instructions-page">
      {/* ---------- Header ---------- */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Survey Instructions</h2>
          <p className="text-sm text-raptor-navy/60 font-body max-w-2xl">Issue a tokenised survey link. Pick the client and site — template version, address, and recipient auto-fill.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => nav('/insurer/instructions/issue')} data-testid="inst-issue-batch-btn"><Send size={16} /> Issue via broker/client</Button>
          <Button variant="outline" onClick={() => nav('/insurer/instructions/bulk')} data-testid="inst-bulk-btn"><Layers size={16} /> Bulk dispatch</Button>
          {isMasterActor && selIds.size > 0 && (
            <Button variant="destructive" onClick={() => setDelOpen(true)} data-testid="inst-hard-delete-btn"><Trash2 size={16} /> Delete {selIds.size} selected</Button>
          )}
          {!creating
            ? <Button variant="gradient" onClick={startIssue} data-testid="inst-open-wizard"><Plus size={16} /> Issue survey</Button>
            : <Button variant="ghost" onClick={cancelIssue} data-testid="inst-close-wizard"><X size={16} /> Cancel</Button>}
        </div>
      </div>

      {/* ================================================================
          Issue wizard
         ================================================================ */}
      {creating && (
        <Card data-testid="issue-wizard">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-base">Issue survey instruction</CardTitle>
              <CardDescription>Guided flow — client & site pickers auto-fill address and recipient.</CardDescription>
            </div>
            <div className="flex items-center gap-2" data-testid="wizard-stepper">
              <StepPill n={1} label="Client & site" icon={<Building2 size={12} />} />
              <ChevronRight size={12} className="text-raptor-navy/40" />
              <StepPill n={2} label="Delivery" icon={<Send size={12} />} />
              <ChevronRight size={12} className="text-raptor-navy/40" />
              <StepPill n={3} label="Review & issue" icon={<Check size={12} />} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* ---- Step 1: Client & Site ---- */}
            {step === 1 && (
              <div className="space-y-4" data-testid="wizard-step-1">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Client</Label>
                    <SearchPicker<ClientRow>
                      items={clients}
                      selectedId={form.client_id}
                      onPick={(c) => setForm((f) => ({ ...f, client_id: c?.id ?? '', site_id: '' }))}
                      labelFor={(c) => c.trading_name || c.name}
                      sublabelFor={(c) => `${c.broker_name ? `Broker: ${c.broker_name}` : 'No broker'} · ${c.site_count ?? 0} site(s)`}
                      placeholder="Search for a client…"
                      testId="picker-client"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Site</Label>
                    <SearchPicker<SiteRow>
                      items={sitesOfClient}
                      selectedId={form.site_id}
                      onPick={(s) => setForm((f) => ({ ...f, site_id: s?.id ?? '' }))}
                      labelFor={(s) => s.name}
                      sublabelFor={(s) => [s.site_reference && `Ref ${s.site_reference}`, s.physical_address].filter(Boolean).join(' · ')}
                      placeholder={form.client_id ? 'Search this client’s sites…' : 'Pick a client first'}
                      emptyLabel={form.client_id ? 'This client has no sites yet.' : 'Pick a client first.'}
                      disabled={!form.client_id}
                      testId="picker-site"
                    />
                  </div>
                </div>

                {/* Auto-fill preview card */}
                {selectedSite && (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-4 space-y-2" data-testid="site-preview">
                    <div className="flex items-center gap-2 text-emerald-900 font-semibold text-sm">
                      <Check size={14} /> Auto-filled from site record
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs font-body text-raptor-navy/80">
                      <div><span className="text-raptor-navy/50">Client:</span> <b>{selectedClient?.trading_name || selectedClient?.name}</b></div>
                      <div><span className="text-raptor-navy/50">Site reference:</span> <span className="font-tabular">{selectedSite.site_reference || '—'}</span></div>
                      <div className="sm:col-span-2"><MapPin size={11} className="inline mr-1 text-raptor-navy/50" /> {selectedSite.physical_address || '—'}</div>
                      {(selectedSite.latitude != null && selectedSite.longitude != null) && (
                        <div className="font-tabular"><span className="text-raptor-navy/50">GPS:</span> {selectedSite.latitude}, {selectedSite.longitude}</div>
                      )}
                      {selectedSite.site_contact_name && (
                        <div><Users size={11} className="inline mr-1 text-raptor-navy/50" /> {selectedSite.site_contact_name}{selectedSite.site_contact_phone ? ` · ${selectedSite.site_contact_phone}` : ''}</div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end pt-2">
                  <Button
                    variant="gradient"
                    onClick={() => setStep(2)}
                    disabled={!form.client_id || !form.site_id}
                    data-testid="wizard-next-1"
                  >
                    Continue <ChevronRight size={14} className="ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* ---- Step 2: Delivery ---- */}
            {step === 2 && (
              <div className="space-y-4" data-testid="wizard-step-2">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Template</Label>
                    <Select
                      value={form.template_id}
                      onChange={(e) => setForm({ ...form, template_id: e.target.value })}
                      data-testid="wizard-template"
                    >
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}{t.current_published_version ? ` (v${t.current_published_version})` : ' (draft)'}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Method</Label>
                    <Select
                      value={form.method}
                      onChange={(e) => setForm({ ...form, method: e.target.value as 'on_site' | 'remote_self' })}
                      data-testid="wizard-method"
                    >
                      <option value="on_site">On-site (surveyor visits)</option>
                      <option value="remote_self">Remote self (client fills)</option>
                    </Select>
                  </div>
                </div>

                {/* --- On-site: surveyor email w/ autocomplete --- */}
                {form.method === 'on_site' && (
                  <div className="space-y-1.5">
                    <Label>Surveyor email <span className="text-raptor-navy/40 text-[10px]">(optional)</span></Label>
                    <Input
                      type="email"
                      list="surveyor-history"
                      value={form.surveyor_email}
                      onChange={(e) => setForm({ ...form, surveyor_email: e.target.value })}
                      placeholder="surveyor@ospreyuma.com"
                      data-testid="wizard-surveyor-email"
                    />
                    <datalist id="surveyor-history">
                      {surveyorHistory.map((e) => <option key={e} value={e} />)}
                    </datalist>
                    <p className="text-[10px] text-raptor-navy/50">Previously used emails autocomplete.</p>
                  </div>
                )}

                {/* --- Remote self: recipient picker --- */}
                {form.method === 'remote_self' && (
                  <div className="space-y-2" data-testid="wizard-remote-recipient">
                    <Label>Recipient</Label>
                    {clientUsers.length > 0 && (
                      <SearchPicker<ClientUserRow>
                        items={clientUsers}
                        selectedId={form.recipient_user_id || null}
                        onPick={pickRecipientUser}
                        labelFor={(u) => u.full_name}
                        sublabelFor={(u) => `${u.role || 'Client user'} · ${u.phone}${u.email ? ` · ${u.email}` : ''}`}
                        placeholder="Pick from client whitelist…"
                        testId="picker-client-user"
                      />
                    )}
                    {clientUsers.length === 0 && !selectedSite?.site_contact_phone && (
                      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        No client-user whitelist and no site contact on file. Add one below or add a Client User under this client.
                      </div>
                    )}
                    <div className="grid md:grid-cols-3 gap-2">
                      <Input
                        placeholder="Recipient name"
                        value={form.recipient_name}
                        onChange={(e) => setForm({ ...form, recipient_name: e.target.value, recipient_user_id: '' })}
                        data-testid="wizard-recipient-name"
                      />
                      <Input
                        placeholder="+27..."
                        value={form.recipient_phone}
                        onChange={(e) => setForm({ ...form, recipient_phone: e.target.value, recipient_user_id: '' })}
                        data-testid="wizard-recipient-phone"
                      />
                      <Input
                        type="email"
                        placeholder="Optional email"
                        value={form.recipient_email}
                        onChange={(e) => setForm({ ...form, recipient_email: e.target.value, recipient_user_id: '' })}
                        data-testid="wizard-recipient-email"
                      />
                    </div>
                    {selectedSite?.site_contact_phone && !form.recipient_phone && (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form,
                          recipient_user_id: '',
                          recipient_name: selectedSite.site_contact_name || '',
                          recipient_phone: selectedSite.site_contact_phone || '',
                          recipient_email: '',
                        })}
                        className="text-xs text-raptor-blue hover:underline inline-flex items-center gap-1"
                        data-testid="wizard-use-site-contact"
                      >
                        <Phone size={11} /> Use site contact ({selectedSite.site_contact_name})
                      </button>
                    )}
                    <p className="text-[10px] text-raptor-navy/50">SMS is not sent automatically — send it from the list once issued.</p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Due date <span className="text-raptor-navy/40 text-[10px]">(optional)</span></Label>
                    <Input
                      type="date"
                      value={form.due_date}
                      onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                      data-testid="wizard-due"
                    />
                  </div>
                  <div className="space-y-1.5 md:row-span-2">
                    <Label>Internal notes <span className="text-raptor-navy/40 text-[10px]">(optional)</span></Label>
                    <Textarea
                      rows={3}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      data-testid="wizard-notes"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <Button variant="ghost" onClick={() => setStep(1)} data-testid="wizard-back-2"><ChevronLeft size={14} className="mr-1" /> Back</Button>
                  <Button
                    variant="gradient"
                    onClick={() => setStep(3)}
                    disabled={!form.template_id}
                    data-testid="wizard-next-2"
                  >
                    Continue <ChevronRight size={14} className="ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* ---- Step 3: Review & issue ---- */}
            {step === 3 && (
              <div className="space-y-4" data-testid="wizard-step-3">
                <div className="rounded-md border border-border/60 bg-raptor-neutral/30 p-4 space-y-2 text-sm font-body">
                  <div className="flex items-center gap-2 text-raptor-navy font-semibold">
                    <Info size={14} className="text-raptor-blue" /> Review & confirm
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                    <div><span className="text-raptor-navy/50">Template:</span> <b>{selectedTemplate?.name}</b>{selectedTemplate?.current_published_version ? ` v${selectedTemplate.current_published_version}` : ''}</div>
                    <div><span className="text-raptor-navy/50">Method:</span> {form.method === 'remote_self' ? 'Remote self' : 'On-site'}</div>
                    <div><span className="text-raptor-navy/50">Client:</span> <b>{selectedClient?.trading_name || selectedClient?.name}</b></div>
                    <div><span className="text-raptor-navy/50">Site:</span> <b>{selectedSite?.name}</b>{selectedSite?.site_reference ? ` · Ref ${selectedSite.site_reference}` : ''}</div>
                    <div className="sm:col-span-2"><span className="text-raptor-navy/50">Address:</span> {selectedSite?.physical_address || '—'}</div>
                    {form.method === 'on_site' && (
                      <div className="sm:col-span-2"><span className="text-raptor-navy/50">Surveyor:</span> {form.surveyor_email || <span className="italic text-raptor-navy/40">unassigned</span>}</div>
                    )}
                    {form.method === 'remote_self' && (
                      <div className="sm:col-span-2"><span className="text-raptor-navy/50">Link will be sent to:</span> {form.recipient_name || <span className="italic text-raptor-navy/40">no recipient set</span>}{form.recipient_phone ? ` · ${form.recipient_phone}` : ''}{form.recipient_email ? ` · ${form.recipient_email}` : ''}</div>
                    )}
                    {form.due_date && <div className="sm:col-span-2"><span className="text-raptor-navy/50">Due date:</span> {form.due_date}</div>}
                    {form.notes && <div className="sm:col-span-2"><span className="text-raptor-navy/50">Notes:</span> {form.notes}</div>}
                  </div>
                </div>

                {form.method === 'remote_self' && !form.recipient_phone && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    No recipient phone set. The instruction will still be issued and the link generated, but you’ll need to share it manually.
                  </div>
                )}

                <div className="flex items-center justify-between pt-2">
                  <Button variant="ghost" onClick={() => setStep(2)} data-testid="wizard-back-3"><ChevronLeft size={14} className="mr-1" /> Back</Button>
                  <Button
                    variant="gradient"
                    onClick={() => void issue()}
                    disabled={issuing || !form.template_id || !form.site_id}
                    data-testid="wizard-issue"
                  >
                    {issuing ? <><Loader2 size={14} className="animate-spin mr-2" /> Issuing…</> : <><Send size={14} className="mr-2" /> Issue &amp; generate link</>}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ================================================================
          List of issued instructions
         ================================================================ */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText size={16} className="text-raptor-blue" /> Issued instructions
            <Badge variant="muted" className="text-[10px]">{items.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
                <tr>
                  {isMasterActor && (
                    <th className="py-3 px-4 text-center w-8">
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        checked={items.length > 0 && items.every((i) => selIds.has(i.id))}
                        onChange={(e) => setSelIds((prev) => {
                          const n = new Set(prev);
                          if (e.target.checked) items.forEach((i) => n.add(i.id));
                          else items.forEach((i) => n.delete(i.id));
                          return n;
                        })}
                        data-testid="inst-select-all"
                      />
                    </th>
                  )}
                  <th className="py-3 px-4 text-left">Instruction #</th>
                  <th className="py-3 px-4 text-left">Client / Site</th>
                  <th className="py-3 px-4 text-left">Site reference</th>
                  <th className="py-3 px-4 text-left">Template</th>
                  <th className="py-3 px-4 text-left">Method</th>
                  <th className="py-3 px-4 text-left">Recipient</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4 text-left">Due</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={isMasterActor ? 10 : 9} className="py-10 text-center text-raptor-navy/50">
                      No instructions issued yet. Click <b>Issue survey</b> to send your first one.
                    </td>
                  </tr>
                )}
                {items.map((i) => {
                  const recipient = i.method === 'remote_self'
                    ? (i.recipient_name || i.recipient_phone || <span className="italic text-raptor-navy/40">unset</span>)
                    : (i.surveyor_email || <span className="italic text-raptor-navy/40">unassigned</span>);
                  return (
                    <tr key={i.id} className="border-t border-border/60 hover:bg-raptor-blue/5" data-testid={`inst-row-${i.id}`}>
                      {isMasterActor && (
                        <td className="py-2 px-4 text-center">
                          <input type="checkbox" checked={selIds.has(i.id)} onChange={() => toggleSel(i.id)} data-testid={`inst-select-${i.id}`} />
                        </td>
                      )}
                      <td className="py-2 px-4 font-tabular text-xs text-raptor-navy/70 whitespace-nowrap">{i.id}</td>
                      <td className="py-2 px-4">
                        <div className="font-semibold text-raptor-navy">{i.client_name}</div>
                        {(i.site_name || i.site_id) && (
                          <div className="text-[11px] text-raptor-navy/60">{i.site_name || i.site_id}</div>
                        )}
                      </td>
                      <td className="py-2 px-4 font-tabular text-xs">{i.site_reference || <span className="text-raptor-navy/30">—</span>}</td>
                      <td className="py-2 px-4 text-xs">
                        <span className="text-raptor-navy">{i.template_name}</span>{' '}
                        <Badge variant="muted" className="text-[10px]">v{i.template_version}</Badge>
                      </td>
                      <td className="py-2 px-4">{methodBadge(i.method)}</td>
                      <td className="py-2 px-4 text-xs">
                        <div className="font-semibold text-raptor-navy">{recipient}</div>
                        {i.method === 'remote_self' && i.recipient_phone && (
                          <div className="text-[11px] text-raptor-navy/60 font-tabular">{i.recipient_phone}</div>
                        )}
                        {i.method === 'remote_self' && i.last_sms_sent_at && (
                          <div className="text-[10px] text-emerald-700">Last SMS: {i.last_sms_sent_at.slice(0, 16).replace('T', ' ')}</div>
                        )}
                      </td>
                      <td className="py-2 px-4">{statusBadge(i.status)}</td>
                      <td className="py-2 px-4 text-xs font-tabular">{i.due_date ? <span className="inline-flex items-center gap-1"><Calendar size={11} /> {i.due_date.slice(0, 10)}</span> : <span className="text-raptor-navy/30">—</span>}</td>
                      <td className="py-2 px-4 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => void copyLink(i.public_link)} title="Copy public link" data-testid={`copy-link-${i.id}`}>
                            <Copy size={12} />
                          </Button>
                          {(i.recipient_email || i.recipient_phone) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => void resend(i.id)}
                              disabled={resendingFor === i.id}
                              title="Resend survey link (email + SMS)"
                              data-testid={`resend-${i.id}`}
                            >
                              {resendingFor === i.id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                            </Button>
                          )}
                          {i.method === 'remote_self' && i.recipient_phone && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => void sendSms(i.id)}
                              disabled={sendingSmsFor === i.id}
                              title={`Send SMS to ${i.recipient_phone}`}
                              data-testid={`send-sms-${i.id}`}
                            >
                              {sendingSmsFor === i.id ? <Loader2 size={12} className="animate-spin" /> : <MessageSquare size={12} />}
                            </Button>
                          )}
                          {i.method === 'remote_self' && i.recipient_email && !i.recipient_phone && (
                            <a
                              href={`mailto:${i.recipient_email}?subject=${encodeURIComponent(i.template_name)}&body=${encodeURIComponent(i.public_link)}`}
                              className="inline-flex items-center justify-center h-7 w-7 rounded hover:bg-raptor-blue/10 text-raptor-navy"
                              title="Email link"
                              data-testid={`email-link-${i.id}`}
                            >
                              <Mail size={12} />
                            </a>
                          )}
                          <Button size="sm" variant="outline" onClick={() => window.open(i.public_link, '_blank')} title="Open survey link" data-testid={`open-link-${i.id}`}>
                            <ExternalLink size={12} />
                          </Button>
                          {i.status !== 'submitted' && i.status !== 'approved' && i.status !== 'cancelled' && (
                            <Button size="sm" variant="ghost" onClick={() => void cancelInstruction(i.id)} title="Cancel survey" data-testid={`cancel-inst-${i.id}`} className="text-red-600 hover:bg-red-50">
                              <Ban size={12} />
                            </Button>
                          )}
                          {i.status === 'cancelled' && (
                            <Button size="sm" variant="ghost" onClick={() => void reactivateInstruction(i.id)} title="Re-activate survey" data-testid={`reactivate-inst-${i.id}`} className="text-emerald-700 hover:bg-emerald-50">
                              <RotateCcw size={12} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Master-only hard-delete confirm */}
      {delOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="inst-delete-modal">
          <Card className="w-full max-w-md border-red-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700"><Trash2 size={18} /> Permanently delete {selIds.size} instruction(s)</CardTitle>
              <CardDescription>
                This hard-deletes the selected instructions <b>and everything linked to them</b> — survey results, risk ratings, overrides, photos and signatures. It cannot be undone and is recorded in the audit log. Type <b>DELETE</b> to confirm.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input value={delText} onChange={(e) => setDelText(e.target.value)} placeholder="Type DELETE" data-testid="inst-delete-confirm-input" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setDelOpen(false); setDelText(''); }} data-testid="inst-delete-cancel">Cancel</Button>
                <Button variant="destructive" onClick={() => void doHardDelete()} disabled={deleting || delText.trim() !== 'DELETE'} data-testid="inst-delete-confirm">
                  {deleting ? 'Deleting…' : 'Delete permanently'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default InstructionsPage;
